import { useCallback, useEffect, useMemo, useState } from 'react';
import { useFeatureFlag } from 'featureFlags';

import BuyFreezesSheet from 'components/Streaks/BuyFreezes/BuyFreezesSheet';
import StreaksIntro, { OPEN_STREAKS_INTRO_EVENT } from 'components/Streaks/Intro/StreaksIntro';
import { deriveStreakView } from 'components/Streaks/model/deriveView';
import {
  streakStore,
  useStreakSnapshot,
  useStreakStore
} from 'components/Streaks/state/streakStore';
import { useStatusAvatars } from 'components/Streaks/state/useStatusAvatars';
import { useStreakClock } from 'components/Streaks/state/useStreakClock';
import { useStreakReducedMotion } from 'components/Streaks/useStreakReducedMotion';

import StreakPanel from './StreakPanel';

/** Opens "How streaks work" (EN-11239). A window event, so a later first-launch intro can listen too. */
export function openStreaksIntro() {
  window.dispatchEvent(new CustomEvent(OPEN_STREAKS_INTRO_EVENT));
}

/**
 * The streak block in the account panel (EN-11162). Collapsed when the panel
 * opens from the avatar; expanded when it opens from the banner tap, which
 * leaves `expandPending` in the store for this block to consume. `onClosePanel`
 * closes the account panel when the freeze sheet's Add funds opens the Cashier.
 */
export default function AccountStreakBlock({
  panelOpen,
  onClosePanel
}: {
  panelOpen: boolean;
  onClosePanel?: () => void;
}) {
  const enabled = useFeatureFlag('streaks');
  const snapshot = useStreakSnapshot();
  const expandPending = useStreakStore((s) => s.expandPending);
  const reducedMotion = useStreakReducedMotion();
  // No guest check needed: a snapshot only exists for a signed-in player.
  // Not gated on panelOpen: the account panel stays mounted through its close
  // slide, and the block must slide out with it, as it was, not vanish first.
  const visible = enabled && !!snapshot;
  const now = useStreakClock(visible && panelOpen);
  const [expanded, setExpanded] = useState(false);
  const [touched, setTouched] = useState(false);

  // Each opening starts collapsed (or expanded from the banner tap); closing
  // leaves the card as it is until the panel unmounts.
  useEffect(() => {
    if (!panelOpen) return;
    setTouched(false);
    setExpanded(false);
  }, [panelOpen]);

  useEffect(() => {
    if (panelOpen && expandPending) {
      setExpanded(true);
      streakStore.consumeExpanded();
    }
  }, [panelOpen, expandPending]);

  const statusAvatars = useStatusAvatars(visible);
  const view = useMemo(
    () => (snapshot ? deriveStreakView(snapshot, now, statusAvatars) : null),
    [snapshot, now, statusAvatars]
  );
  const onToggle = useCallback(() => {
    setTouched(true);
    setExpanded((e) => !e);
  }, []);
  const onBuy = useCallback(() => streakStore.setBuyOpen(true), []);

  if (!visible || !view || !snapshot) return null;
  return (
    <>
      <StreakPanel
        view={view}
        snapshot={snapshot}
        expanded={expanded}
        animateIn={touched}
        reducedMotion={reducedMotion}
        onToggle={onToggle}
        onBuy={onBuy}
        onHowItWorks={openStreaksIntro}
      />
      <BuyFreezesSheet onLeave={onClosePanel} />
      <StreaksIntro snapshot={snapshot} />
    </>
  );
}
